/**
 * Booking Service - Frontend API calls for booking operations
 */

import api from "@/shared/api";
import { logError } from "@/shared/utils/logger.js";

/**
 * Get available teams for a date and load type
 * @param {Object} params - Query parameters
 * @param {string} params.moveDate - Move date (YYYY-MM-DD)
 * @param {string} params.loadType - Load type
 * @param {string} params.timeWindow - Time window type
 * @param {string} params.vehicleType - Vehicle type (optional)
 * @returns {Promise<Object>} Available teams
 */
export const getAvailableTeams = async (params) => {
  try {
    const response = await api.get("/teams/available", { params });
    return response;
  } catch (error) {
    logError("Error fetching available teams", {
      params,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch available teams",
    };
  }
};

/**
 * Create booking
 * @param {Object} bookingData - Booking data
 * @param {string} bookingData.quoteId - Quote ID
 * @param {string} bookingData.orderId - Order ID
 * @param {string} bookingData.moveDate - Move date (YYYY-MM-DD)
 * @param {Object} bookingData.pickupTimeWindow - Pickup time window
 * @param {string} bookingData.teamId - Team ID (optional)
 * @param {Object} bookingData.notes - Special notes
 * @param {Object} bookingData.addOns - Add-ons
 * @returns {Promise<Object>} Booking creation result
 */
export const createBooking = async (bookingData) => {
  try {
    const response = await api.post("/bookings/create", bookingData);
    return response;
  } catch (error) {
    logError("Error creating booking", {
      bookingData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to create booking",
    };
  }
};

/**
 * Get bookings with optional filters
 * @param {Object} filters - Filter criteria
 * @param {string} filters.startDate - Start date (YYYY-MM-DD)
 * @param {string} filters.endDate - End date (YYYY-MM-DD)
 * @param {string} filters.teamId - Team ID
 * @param {string} filters.status - Booking status
 * @param {string} filters.quoteId - Quote ID
 * @returns {Promise<Object>} Bookings list
 */
export const getBookings = async (filters = {}) => {
  try {
    const response = await api.get("/bookings", { params: filters });
    return response;
  } catch (error) {
    logError("Error fetching bookings", {
      filters,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch bookings",
    };
  }
};

/**
 * Get booking details
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Booking details
 */
export const getBooking = async (orderId) => {
  try {
    // Add timestamp to prevent browser caching
    const response = await api.get(`/bookings/${orderId}?_t=${Date.now()}`);
    return response;
  } catch (error) {
    logError("Error fetching booking", {
      orderId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch booking",
    };
  }
};

/**
 * Get booking confirmation templates
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Confirmation templates
 */
export const getConfirmationTemplates = async (orderId) => {
  try {
    const response = await api.get(
      `/bookings/${orderId}/confirmation-templates`
    );
    return response;
  } catch (error) {
    logError("Error fetching confirmation templates", {
      orderId,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch confirmation templates",
    };
  }
};

/**
 * Update confirmation status
 * @param {string} orderId - Order ID
 * @param {Object} confirmationData - Confirmation data
 * @param {boolean} confirmationData.whatsappSent - WhatsApp sent
 * @param {boolean} confirmationData.emailSent - Email sent
 * @param {boolean} confirmationData.reminderSent - Reminder sent
 * @returns {Promise<Object>} Update result
 */
export const updateConfirmation = async (orderId, confirmationData) => {
  try {
    const response = await api.put(
      `/bookings/${orderId}/confirmations`,
      confirmationData
    );
    return response;
  } catch (error) {
    logError("Error updating confirmation", {
      orderId,
      confirmationData,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to update confirmation",
    };
  }
};

/**
 * Update booking
 * @param {string} orderId - Order ID
 * @param {Object} updates - Update data
 * @param {string} updates.moveDate - Move date (YYYY-MM-DD)
 * @param {Object|string} updates.pickupTimeWindow - Pickup time window
 * @param {string} updates.teamId - Team ID (optional)
 * @param {Object} updates.notes - Special notes
 * @param {Object} updates.addOns - Add-ons
 * @returns {Promise<Object>} Update result
 */
export const updateBooking = async (orderId, updates) => {
  try {
    const response = await api.put(`/bookings/${orderId}`, updates);
    return response;
  } catch (error) {
    logError("Error updating booking", {
      orderId,
      updates,
      error: error.message,
      status: error.response?.status,
    });
    return {
      success: false,
      error: error.message || "Failed to update booking",
    };
  }
};
